/**
 * Training tracks (learning paths) — API client.
 *
 * Learner: `getMyPath` returns every active track targeting the caller with
 * per-level status and per-module progress (Phase 1 tracks are single-level,
 * so the hub renders a flat path). Admin (training.manage): track CRUD plus
 * replacing the ordered module list of a track's first level.
 */

import { fetchApi } from "@/lib/api";

// The level state machine's vocabulary. Phase 1 tracks carry no gate rows,
// so `gates_pending` is unreachable until Phase 2 turns gates on.
export type LevelStatus = "locked" | "unlocked" | "in_progress" | "gates_pending" | "complete";

export interface PathModule {
  module_id: string;
  title: string;
  /** The module's kind signal (guided tour / how-to / video / artifact /
   *  to-do list) — drives the hub's type badge. */
  source_type: string | null;
  seq: number;
  required: boolean;
  percent: number;
  started: boolean;
  /** Two-axis predicate: content consumed AND the attached quiz (if any) passed. */
  completed: boolean;
  /** Historical content-completion stamp — survives a quiz-driven reopen. */
  completed_at: string | null;
  /** The module carries a quiz with questions the caller hasn't passed. */
  quiz_pending: boolean;
}

export interface GateCrumb {
  name: string;
  /** The node's slug chain — deep-links as /foundations/browse/{path}. */
  path: string;
}

/** Where a gate's measure comes from: the Foundations standard that owns the
 *  competency plus its ancestor node chain (pillar → leaf). Rendered as a
 *  breadcrumb; segments link into Foundations only for viewers holding the
 *  `foundations` capability. */
export interface GateLineage {
  standard_id: string;
  standard_title: string;
  crumbs: GateCrumb[];
}

export interface PathGate {
  id: string;
  gate_type: string;
  /** The competency's canonical registry name (e.g. "# Sales Won"). */
  label: string;
  /** Trainer-set target count; null only on a misconfigured gate. */
  threshold: number | null;
  /** The learner's live count toward the threshold (or the count recorded when
   *  the gate latched); null when not yet evaluated (e.g. a locked level). */
  value: number | null;
  /** Latched assessment — met once, never re-demanded. */
  met: boolean;
  /** Null when the competency no longer resolves. */
  lineage: GateLineage | null;
}

export interface PathLevel {
  id: string;
  seq: number;
  title: string;
  description: string | null;
  status: LevelStatus;
  /** Historical stamp: when the level was first earned. Present alongside a
   *  non-complete status when new required material reopened the level. */
  completed_at: string | null;
  modules: PathModule[];
  /** Metric (competence) gates on the level — quiz gates surface via
   *  quiz_pending on their module rows instead. */
  gates?: PathGate[];
}

export interface PathTrack {
  id: string;
  slug: string;
  title: string;
  levels: PathLevel[];
  /** Latest level-latch stamp once every level is complete; null while outstanding. */
  completed_at: string | null;
  /** Brand deadline binding this track for the learner (YYYY-MM-DD), or null.
   *  The same date the completion report holds them to. */
  deadline: string | null;
}

export interface MyPath {
  tracks: PathTrack[];
}

export interface TrackTargeting {
  roles: string[] | null;
  tenants: string[] | null;
  /** Per-brand to-do deadline (`{ tenantSlug: "YYYY-MM-DD" }`): learners
   *  enrolled through that brand get every to-do list in the track due on
   *  that date instead of the modules' own offsets. Keys must be targeted
   *  brands (any brand when `tenants` is null). Absent on older tracks. */
  deadlines?: Record<string, string> | null;
  /** "New hires only": reach intake hires in these roles and brands who
   *  finish onboarding from the moment this is turned on. */
  new_hires_only?: boolean;
  /** Read-only: when "New hires only" was turned on (the server stamps it). */
  new_hires_since?: string | null;
}

export interface AdminLevelModule {
  module_id: string;
  title: string;
  seq: number;
  required: boolean;
}

export interface AdminGate {
  id: string;
  gate_type: string;
  competency_id: string | null;
  threshold: number | null;
  /** The competency's canonical registry name; null when it no longer resolves. */
  label: string | null;
  /** Fail-loud surface: why this gate cannot evaluate (learners hold at it). */
  config_issue: string | null;
  lineage: GateLineage | null;
}

export interface AdminLevel {
  id: string;
  seq: number;
  title: string;
  description: string | null;
  modules: AdminLevelModule[];
  gates?: AdminGate[];
}

export interface AdminTrack {
  id: string;
  slug: string;
  title: string;
  targeting: TrackTargeting;
  is_active: boolean;
  /** First turn-on, never cleared: off + null = "not live yet", off + set =
   *  archived (it was live once). */
  went_live_at: string | null;
  levels: AdminLevel[];
  /** People the track reaches now — the same count as the Track settings
   *  preview and the go-live confirmation (current targeting over the report
   *  population, Central band included). 0 while switched off. */
  reach_count: number;
  /** Active, person-compatible competencies on the track's linked standards
   *  with no gate attached — the amber hint names each one and offers a
   *  one-click "set threshold" that opens the gate dialog preselected.
   *  Populated by the list read. */
  ungated_competencies?: { competency_id: string; name: string }[];
}

export interface GateOption {
  competency_id: string;
  label: string;
  definition_slug: string;
  default_threshold: number | null;
  standard_title: string;
  already_gated: boolean;
  // False = the competency's definition isn't a KPI's definition counted
  // per person, so it can't back a gate; shown disabled with that reason
  // rather than hidden.
  person_compatible: boolean;
  /** Groups the picker by Foundations node — a mis-filed measure is visible
   *  before it's ever attached. */
  lineage: GateLineage | null;
}

export async function getMyPath(): Promise<MyPath> {
  return fetchApi("/training/tracks/my-path") as Promise<MyPath>;
}

export async function listTracks(): Promise<AdminTrack[]> {
  return fetchApi("/training/admin/tracks") as Promise<AdminTrack[]>;
}

export async function createTrack(body: {
  title: string;
  targeting?: Partial<TrackTargeting>;
}): Promise<AdminTrack> {
  return fetchApi("/training/admin/tracks", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<AdminTrack>;
}

export async function updateTrack(
  trackId: string,
  body: { title?: string; targeting?: Partial<TrackTargeting>; is_active?: boolean },
): Promise<AdminTrack> {
  return fetchApi(`/training/admin/tracks/${trackId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  }) as Promise<AdminTrack>;
}

/** What saving a targeting would do — the Track settings warning. Removed
 *  people leave the track (their open to-dos for it close; progress is kept). */
export interface ReachPreview {
  reaches: number;
  added: number;
  removed: { name: string; brand: string | null }[];
}

export async function previewTrackReach(body: {
  track_id: string | null;
  targeting: Pick<TrackTargeting, "roles" | "tenants">;
}): Promise<ReachPreview> {
  return fetchApi("/training/admin/tracks/reach-preview", {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<ReachPreview>;
}

/** Acquisition date per promoted brand slug (`tenant_integrations.acquisition_date`)
 *  — the one-click SUGGESTION beside a brand's deadline cell. Never a default; the
 *  UI hides it once the date has passed. */
/** Who turning the track on would reach right now — the shared reach count
 *  (current targeting, Central band included), same as the card. */
export interface TrackReach {
  people: number;
  brands: number;
}

export async function getTrackReach(trackId: string): Promise<TrackReach> {
  return fetchApi(`/training/admin/tracks/${trackId}/reach`) as Promise<TrackReach>;
}

export async function getBrandCloseDates(): Promise<Record<string, string>> {
  return fetchApi("/training/admin/tracks/brand-close-dates") as Promise<Record<string, string>>;
}

export async function setTrackModules(
  trackId: string,
  modules: { module_id: string; required: boolean }[],
): Promise<AdminTrack> {
  return fetchApi(`/training/admin/tracks/${trackId}/modules`, {
    method: "PUT",
    body: JSON.stringify({ modules }),
  }) as Promise<AdminTrack>;
}

export async function listGateOptions(trackId: string): Promise<GateOption[]> {
  return fetchApi(`/training/admin/tracks/${trackId}/gate-options`) as Promise<GateOption[]>;
}

export async function addGate(
  trackId: string,
  body: { competency_id: string; threshold: number },
): Promise<AdminTrack> {
  return fetchApi(`/training/admin/tracks/${trackId}/gates`, {
    method: "POST",
    body: JSON.stringify(body),
  }) as Promise<AdminTrack>;
}

export async function updateGate(
  trackId: string,
  gateId: string,
  body: { threshold: number },
): Promise<AdminTrack> {
  return fetchApi(`/training/admin/tracks/${trackId}/gates/${gateId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  }) as Promise<AdminTrack>;
}

export async function deleteGate(trackId: string, gateId: string): Promise<void> {
  await fetchApi(`/training/admin/tracks/${trackId}/gates/${gateId}`, { method: "DELETE" });
}
